import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'context/AuthContext';
import { registrarOnboarding } from 'services/iniciativa-action.service';
import { Step } from 'types/tutorial';
import { normalizeApiHtmlContent, preloadImagesFromHtmlBlocks } from 'utils/html-content';
import './style.scss';

interface OnboardingModalProps {
  steps: Step[];
  onClose: () => void;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ steps, onClose }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isStepContentReady, setIsStepContentReady] = useState(false);
  const textContainerRef = useRef<HTMLDivElement>(null);

  const currentStepHtml = useMemo(
    () => normalizeApiHtmlContent(steps[currentStep]?.text || ''),
    [steps, currentStep]
  );

  useEffect(() => {
    const htmlBlocks = steps.map((step) => step?.text || '');

    if (htmlBlocks.length > 0) {
      preloadImagesFromHtmlBlocks(htmlBlocks);
    }
  }, [steps]);

  useEffect(() => {
    const container = textContainerRef.current;

    if (!container) {
      setIsStepContentReady(true);
      return;
    }

    setIsStepContentReady(false);

    const images = Array.from(container.querySelectorAll('img'));

    if (images.length === 0) {
      const raf = requestAnimationFrame(() => setIsStepContentReady(true));
      return () => cancelAnimationFrame(raf);
    }

    let loadedCount = 0;
    let isMounted = true;

    const handleAssetReady = () => {
      loadedCount += 1;
      if (isMounted && loadedCount >= images.length) {
        setIsStepContentReady(true);
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        handleAssetReady();
      } else {
        img.addEventListener('load', handleAssetReady);
        img.addEventListener('error', handleAssetReady);
      }
    });

    return () => {
      isMounted = false;
      images.forEach((img) => {
        img.removeEventListener('load', handleAssetReady);
        img.removeEventListener('error', handleAssetReady);
      });
    };
  }, [currentStepHtml]);

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      if (user?._id) {
        try {
          await registrarOnboarding(user._id);
        } catch (error) {
        }
      }

      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (steps.length === 0) return null;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="onboarding-modal">
      <div className="onboarding-modal__card">
        <div className="onboarding-modal__image-section">
          {isFirstStep && (
            <div className="onboarding-modal__first-step-images">
            </div>
          )}
          {!isFirstStep && (
            <div className="onboarding-modal__image-section">
            </div>
          )}
        </div>

        <div 
          ref={textContainerRef}
          className={`onboarding-modal__text ${isStepContentReady ? 'is-ready' : ''}`}
          dangerouslySetInnerHTML={{ __html: currentStepHtml }}
        />

        <div className="onboarding-modal__indicators">
          {steps.map((_, index) => (
            <button
              key={index}
              className={`onboarding-modal__indicator ${index === currentStep ? 'active' : ''}`}
              onClick={() => setCurrentStep(index)}
              aria-label={`Ir para passo ${index + 1}`}
            />
          ))}
        </div>

        <div className="onboarding-modal__actions">
          {!isFirstStep && (
            <button 
              className="onboarding-modal__btn onboarding-modal__btn--secondary"
              onClick={handlePrevious}
            >
              VOLTAR
            </button>
          )}
          <button 
            className="onboarding-modal__btn onboarding-modal__btn--primary"
            onClick={handleNext}
          >
            {isFirstStep ? 'INICIAR TUTORIAL' : isLastStep ? 'FECHAR' : 'CONTINUAR'}
          </button>
        </div>
      </div>
    </div>
  );
};
